import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Download,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
  NotebookText,
  Sparkles,
  X,
} from "lucide-react";
import clsx from "clsx";
import {
  Assistant,
  Library,
  type AssistantAction,
  type ConversationDetail,
  type DocumentCard,
} from "@/api/assistant";
import { apiErrorMessage } from "@/api/client";
import { toast } from "@/lib/feedback";
import { fmtDay } from "@/lib/format";
import { useUIStore } from "@/store/ui";

/** A compact line for a tool step ("Searching the web", "Reading the deal"). */
export function ToolStep({ label, state }: { label: string; state: "running" | "done" | "error" }) {
  const web = /web|page|Read http/i.test(label);
  const Icon = state === "running" ? Loader2 : web ? Globe : Sparkles;
  return (
    <div
      className={clsx(
        "flex items-center gap-2 text-sm",
        state === "error" ? "text-accent-amber" : "text-mut-500",
      )}
    >
      <Icon className={clsx("w-3.5 h-3.5 shrink-0", state === "running" && "animate-spin")} />
      <span className="truncate">{label}</span>
    </div>
  );
}

const STATUS: Record<
  AssistantAction["status"],
  { label: string; card: string; badge: string; Icon: typeof Check }
> = {
  pending: {
    label: "Needs your OK",
    card: "border-accent-violet/30 bg-accent-violet/[0.06]",
    badge: "bg-accent-violet/15 text-accent-violet",
    Icon: Sparkles,
  },
  applied: {
    label: "Applied",
    card: "border-accent-green/40 bg-accent-green/[0.07]",
    badge: "bg-accent-green text-white",
    Icon: Check,
  },
  dismissed: {
    label: "Dismissed",
    card: "border-fg/[0.08] bg-ink-900 opacity-75",
    badge: "bg-fg/[0.08] text-mut-500",
    Icon: X,
  },
  failed: {
    label: "Couldn't apply",
    card: "border-accent-amber/40 bg-accent-amber/[0.07]",
    badge: "bg-accent-amber text-white",
    Icon: X,
  },
};

function timeOf(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
}

/** A CRM change the assistant proposed. Nothing happens until the user applies it. */
export function ProposalCard({ action }: { action: AssistantAction }) {
  const qc = useQueryClient();
  const openDeal = useUIStore((s) => s.openDeal);
  // The result of a click here wins; otherwise show what the server last said.
  const [local, setLocal] = useState<AssistantAction | null>(null);
  const current = local ?? action;

  const settle = (a: AssistantAction) => {
    setLocal(a);
    // Keep every cached copy of the chat in step, so the card stays resolved
    // after leaving the page and coming back.
    qc.setQueriesData<ConversationDetail>({ queryKey: ["assistant-conversation"] }, (old) =>
      old && old.actions.some((x) => x.id === a.id)
        ? { ...old, actions: old.actions.map((x) => (x.id === a.id ? a : x)) }
        : old,
    );
    void qc.invalidateQueries({ queryKey: ["inbox"] });
    if (a.status === "applied") {
      toast.ok(`Applied: ${a.summary}`);
      void qc.invalidateQueries({ queryKey: ["deals"] });
      void qc.invalidateQueries({ queryKey: ["my-work"] });
      const dealId = a.result?.deal_id;
      if (typeof dealId === "string") void qc.invalidateQueries({ queryKey: ["deal", dealId] });
    } else if (a.status === "failed") {
      toast.err(`Couldn't apply: ${String(a.result?.error ?? "unknown error")}`);
    }
  };
  const onError = (e: unknown) => {
    toast.err(apiErrorMessage(e));
    // Most likely already resolved elsewhere (another tab): pick up the real state.
    void qc.invalidateQueries({ queryKey: ["assistant-conversation"] });
    void qc.invalidateQueries({ queryKey: ["inbox"] });
  };
  const apply = useMutation({
    mutationFn: () => Assistant.applyAction(current.id),
    onSuccess: settle,
    onError,
  });
  const dismiss = useMutation({
    mutationFn: () => Assistant.dismissAction(current.id),
    onSuccess: settle,
    onError,
  });

  const look = STATUS[current.status];
  const pending = current.status === "pending";
  const dealId = typeof current.result?.deal_id === "string" ? current.result.deal_id : null;
  return (
    <div className={clsx("rounded-2xl border p-4 transition-colors duration-300", look.card)}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <span
            key={current.status}
            className={clsx(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-[.06em] animate-fade-up",
              look.badge,
            )}
          >
            <look.Icon className="h-3 w-3" />
            {look.label}
            {!pending && current.resolved_at && (
              <span className="font-normal normal-case tracking-normal opacity-80">
                · {timeOf(current.resolved_at)}
              </span>
            )}
          </span>
          <div
            className={clsx(
              "mt-1.5 text-md font-medium",
              current.status === "dismissed" && "line-through text-mut-500",
            )}
          >
            {current.summary}
          </div>
          {current.status === "failed" && current.result?.error != null && (
            <div className="mt-1 text-sm text-accent-amber">{String(current.result.error)}</div>
          )}
          {current.status === "applied" && current.result?.note != null && (
            <div className="mt-1 text-sm text-mut-500">{String(current.result.note)}</div>
          )}
        </div>
      </div>
      {pending ? (
        <div className="mt-3 flex gap-2">
          <button
            className="btn btn-primary"
            disabled={apply.isPending || dismiss.isPending}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Applying…" : "Apply"}
          </button>
          <button
            className="btn"
            disabled={apply.isPending || dismiss.isPending}
            onClick={() => dismiss.mutate()}
          >
            Dismiss
          </button>
        </div>
      ) : (
        current.status === "applied" &&
        dealId && (
          <div className="mt-2">
            <button
              className="text-sm font-medium text-accent-azure hover:underline"
              onClick={() => openDeal(dealId)}
            >
              Open the deal →
            </button>
          </div>
        )
      )}
    </div>
  );
}

/** A document the assistant produced (PDF or research note). */
export function DocumentCardView({ document: d }: { document: DocumentCard }) {
  const isPdf = d.kind === "pdf";
  const Icon = isPdf ? FileText : NotebookText;
  const [busy, setBusy] = useState(false);
  const open = async (download: boolean) => {
    setBusy(true);
    try {
      await Library.openPdf(d.id, download, `${d.title}.pdf`);
    } catch (e) {
      toast.err(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-fg/[0.08] bg-ink-900 p-4 flex items-start gap-3 shadow-soft">
      <span
        className="grid place-items-center w-11 h-11 rounded-xl shrink-0 text-white"
        style={{ background: isPdf ? "var(--grad)" : "rgb(var(--cyan))" }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-md font-semibold truncate">{d.title}</div>
        <div className="text-sm text-mut-500">
          {isPdf ? "PDF" : "Research note"}
          {d.company ? ` · ${d.company}` : ""} · {fmtDay(d.created_at)}
        </div>
        {d.summary && <p className="mt-1.5 text-sm text-mut-300 line-clamp-2">{d.summary}</p>}
        <div className="mt-3 flex gap-2">
          {isPdf ? (
            <>
              <button className="btn" disabled={busy} onClick={() => void open(false)}>
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={() => void open(true)}>
                <Download className="w-3.5 h-3.5" /> Download
              </button>
            </>
          ) : (
            <a className="btn" href={`/library?doc=${d.id}`}>
              <NotebookText className="w-3.5 h-3.5" /> View in Library
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
